import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkTokenValidity, subscribeToAuthChanges } from '../utils/api';
import * as authService from '../api/auth';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    // Fonction pour initialiser l'état d'authentification
    const initializeAuth = () => {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');

        if (token && checkTokenValidity()) {
            if (userData) {
                try {
                    const parsedUser = JSON.parse(userData);
                    setUser(parsedUser);
                } catch (error) {
                    console.error('Error parsing user data:', error);
                    handleInvalidAuth();
                }
            }
        } else {
            handleInvalidAuth();
        }

        setIsLoading(false);
    };

    // Fonction pour gérer l'authentification invalide
    const handleInvalidAuth = () => {
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('refreshToken');
    };

    // Initialiser l'authentification au montage du composant
    useEffect(() => {
        initializeAuth();

        // S'abonner aux changements d'authentification depuis les intercepteurs
        const unsubscribe = subscribeToAuthChanges((isAuth) => {
            if (!isAuth) {
                setUser(null);
            }
        });

        return unsubscribe;
    }, []);

    // Fonction de connexion avec blocage des commerciaux
    const login = async (username, password) => {
        try {
            const data = await authService.login(username, password);

            const userData = {
                id: data.id,
                username: data.username,
                fullName: data.fullName,
                email: data.email,
                role: data.roles?.[0]?.replace('ROLE_', '').toLowerCase() || 'user',
                roles: data.roles,
                isFirstLogin: data.firstLogin || false
            };

            // VÉRIFICATION : Bloquer les commerciaux
            // const userIsSalesRep = userData.roles?.includes('ROLE_SALES_REP');
            
            // if (userIsSalesRep) {
            //     // Ne pas sauvegarder les données et retourner une erreur
            //     return {
            //         success: false,
            //         message: "L'accès est restreint pour les commerciaux."
            //     };
            // }

            // Si ce n'est pas un commercial, procéder normalement
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(userData));

            if (data.refreshToken) {
                localStorage.setItem('refreshToken', data.refreshToken);
            }

            setUser(userData);

            if (data.firstLogin) {
                navigate('/first-login');
            } else {
                navigate('/dashboard');
            }

            return { success: true };
        } catch (error) {
            return {
                success: false,
                message: error.message || 'Login failed. Please check your credentials.'
            };
        }
    };

    // Fonction de changement de mot de passe lors de la première connexion
    const changePasswordFirstLogin = async (oldPassword, newPassword) => {
        try {
            await authService.changePasswordFirstLogin(user.id, oldPassword, newPassword);

            const updatedUser = { ...user, isFirstLogin: false };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            setUser(updatedUser);

            navigate('/dashboard');
            return { success: true };
        } catch (error) {
            return {
                success: false,
                message: error.message || 'Failed to change password. Please try again.'
            };
        }
    };

    // Fonction de déconnexion
    const logout = () => {
        authService.logout();
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('refreshToken');
        setUser(null);
        navigate('/login');
    };

    // Fonction pour mettre à jour les données utilisateur
    const updateUser = (updatedUserData) => {
        const newUserData = { ...user, ...updatedUserData };
        localStorage.setItem('user', JSON.stringify(newUserData));
        setUser(newUserData);
    };

    // Fonction pour mettre à jour le statut de première connexion
    const updateFirstLogin = () => {
        if (user) {
            const updatedUser = { ...user, isFirstLogin: false };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            setUser(updatedUser);
        }
    };

    // Calculer les propriétés d'authentification
    const isAuthenticated = !!user;
    const isFirstLogin = user?.isFirstLogin || false;
    const isAdmin = user?.roles?.includes('ROLE_ADMIN') || user?.role === 'admin';
    const isManager = user?.roles?.includes('ROLE_MANAGER') ||
        user?.roles?.includes('ROLE_ADMIN') ||
        user?.role === 'manager' ||
        user?.role === 'admin';
    const isDistributor = user?.roles?.includes('ROLE_DISTRIBUTOR') ||
        user?.role === 'distributor';

    const isSalesRep = user?.roles?.includes('ROLE_SALES_REP');

    const contextValue = {
        // États
        user,
        isLoading,
        isAuthenticated,
        isFirstLogin,

        // Rôles
        isAdmin,
        isManager,
        isDistributor,
        isSalesRep,

        // Fonctions
        login,
        logout,
        changePasswordFirstLogin,
        updateUser,
        updateFirstLogin
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

// Hook personnalisé pour utiliser le contexte d'authentification
const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default useAuth;